import { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router";
import { ErrorRetry } from "../../components/common/ErrorRetry";
import { useAccountSubscriptions } from "../../hooks/useSubscribtionDashboard";
import {
  buildUserRow,
  DEFAULT_SEARCH_BY,
  HEADER_CELL_STYLES,
  PAPER_STYLES,
  TABLE_COLUMNS,
} from "../../sections/subscriptionDashboard/users/usersScreen.constants";
import {
  EmptyTableRow,
  SearchBar,
  UserRow,
  UsersHeader,
} from "../../sections/subscriptionDashboard/users/UsersScreenSections";
import { getErrorMessage } from "../../utils/error";

export default function UsersScreen() {
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const navigate = useNavigate();

  const trimmedQuery = searchQuery.trim();
  const offset = page * limit;

  const queryParams = useMemo(
    () => ({
      limit,
      offset,
      ...(trimmedQuery
        ? {
            searchBy: DEFAULT_SEARCH_BY,
            keyword: trimmedQuery,
          }
        : {}),
    }),
    [limit, offset, trimmedQuery],
  );

  const {
    data: usersData,
    isLoading: usersLoading,
    isError: usersError,
    error: usersErrorObj,
    refetch: usersRefetch,
  } = useAccountSubscriptions(queryParams);

  const users = useMemo(() => usersData?.data?.data ?? [], [usersData]);
  const userRows = useMemo(() => users.map(buildUserRow), [users]);
  const totalCount =
    usersData?.data?.meta?.total ?? usersData?.data?.meta?.totalCount ?? 0;

  const handleViewUser = (accountId) => {
    if (!accountId) return;
    navigate(`/subscritionDashboard/users/${accountId}`);
  };

  return (
    <Box>
      <UsersHeader />

      <SearchBar
        value={searchQuery}
        onChange={(value) => {
          setSearchQuery(value);
          setPage(0);
        }}
      />

      {usersError ? (
        <ErrorRetry
          message={getErrorMessage(usersErrorObj)}
          onRetry={() => usersRefetch()}
        />
      ) : (
        <Paper elevation={0} sx={PAPER_STYLES}>
          <TableContainer>
            <Table sx={{ minWidth: 900 }}>
              <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                <TableRow>
                  {TABLE_COLUMNS.map((column) => (
                    <TableCell key={column} sx={HEADER_CELL_STYLES}>
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {usersLoading ? (
                  <EmptyTableRow message="Loading users..." />
                ) : userRows.length === 0 ? (
                  <EmptyTableRow message="No users found." />
                ) : (
                  userRows.map((row) => (
                    <UserRow
                      key={row.id}
                      row={row}
                      onViewUser={handleViewUser}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            rowsPerPage={limit}
            onRowsPerPageChange={(event) => {
              setLimit(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}
    </Box>
  );
}
